import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory } from "react-router";

import { FixedSaveBar, SaveBarMode } from "components/form";
import { BackButton, Grid, ViewStack } from "components/grid";
import Role from "constants/role";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { PageWrapper } from "routes/reports/exports";
import { actions$ } from "store/action-stream";
import { CRUDAction, IEntityCreateSuccess, RequestAction } from "store/entities/actions";
import { selectLoggedInAgentPermission } from "store/entities/agents";
import { ContactFormActionNames, ContactFormActions } from "store/entities/contact-forms/actions";
import { IContactForm } from "store/entities/contact-forms/interfaces";
import { selectDefaultTeamId } from "store/entities/license";
import { IActionWithPayload } from "store/helper";

import SettingsViewContainer from "../../ViewContainer";
import { SettingsRoutes } from "../../constants";
import { ContentWrapper } from "../../styled";
import { DEFAULT_SETTINGS } from "../constants";
import { ValidationResult, validate } from "../validation";

import { ContactForm } from "./ContactForm";

interface IProps {
  settingsOverride?: Partial<IContactForm>;
}

export const AddContactForm: FC<IProps> = ({ settingsOverride = {} }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const backButtonRef = useRef(null);

  const defaultTeamId = useSelector(selectDefaultTeamId);
  const isAdmin = useSelector(selectLoggedInAgentPermission) === Role.Owner;

  const [isContactFormChanged, toggleContactFormChanged] = useState(settingsOverride.name !== undefined);
  const [isSaving, toggleIsSaving] = useState(false);
  const [livechatGroupChanged, toggleLivechatGroupChanged] = useState(false);

  const settings = { teamID: defaultTeamId, ...DEFAULT_SETTINGS, ...settingsOverride };
  const [contactForm, setContactForm] = useState<Partial<IContactForm>>(settings);
  const [formValidations, setFormValidations] = useState<{ [Key in keyof IContactForm]?: [boolean, ValidationResult] }>(
    {
      name: [settingsOverride.name !== undefined, validate("name", settings.name || "")],
      livechatGroupIDs: [
        true,
        {
          type: "required",
        },
      ],
    }
  );

  const oldConsentMessage = DEFAULT_SETTINGS.consentMessage;

  // Update the preview when all fields are ok (except name, because it's not required to show the preview)
  const shouldUpdatePreview = Object.entries(formValidations).every(
    ([key, value]) => key === "name" || value[1].type === "ok"
  );
  const isFormValid = shouldUpdatePreview && formValidations.name?.[1]?.type === "ok";
  const saveBarMode = isSaving ? SaveBarMode.Saving : isContactFormChanged ? SaveBarMode.OfferSave : SaveBarMode.Hidden;

  const handleBack = useCallback(() => {
    trackEngagement(
      settingsOverride.name !== undefined
        ? "Settings: Contact forms duplication back button pressed"
        : "Settings: Contact forms creation back button pressed",
      EventPlace.Settings
    );
    history.push(SettingsRoutes.ContactForms);
  }, [history, settingsOverride.name]);

  const handleChange = useCallback(
    (fieldName: keyof IContactForm) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        toggleContactFormChanged(true);
        setContactForm({ ...contactForm, [fieldName]: event.target.value });
      },
    [contactForm]
  );

  const handleConsentChange = useCallback(
    (item: "checkbox" | "info" | "disabled" | undefined) => {
      toggleContactFormChanged(true);
      setContactForm({
        ...contactForm,
        consentMode: item,
        ...(item === "disabled" ? { consentMessage: oldConsentMessage } : {}),
      });
      if (item === "disabled") {
        setFormValidations({ ...formValidations, consentMessage: [false, { type: "ok" }] });
      }
    },
    [contactForm, formValidations, oldConsentMessage]
  );

  const handleLivechatGroupChange = useCallback(
    (groupIDs: string[]) => {
      toggleContactFormChanged(true);
      toggleLivechatGroupChanged(true);
      setContactForm({
        ...contactForm,
        livechatGroupIDs: groupIDs,
      });
      setFormValidations({
        ...formValidations,
        livechatGroupIDs: [
          true,
          {
            type: groupIDs.length > 0 ? "ok" : "required",
          },
        ],
      });
    },
    [contactForm, formValidations]
  );

  const handleTeamChange = useCallback(
    (teamID: string) => {
      toggleContactFormChanged(true);
      setContactForm({ ...contactForm, teamID });
    },
    [contactForm]
  );
  const handleSubmit = useCallback(() => {
    toggleIsSaving(true);
    trackEngagement(
      settingsOverride.name ? "Settings: Contact form duplicated" : "Settings: Contact form created",
      EventPlace.Settings
    );

    if (livechatGroupChanged) {
      trackEngagement("Settings: Contact form LiveChat groups assigned while adding", EventPlace.Settings, {
        livechatGroupCount: (contactForm.livechatGroupIDs || []).length,
      });
    }
    dispatch(ContactFormActions.create({ value: contactForm }));
  }, [contactForm, dispatch, livechatGroupChanged, settingsOverride.name]);

  const handleValidation = useCallback(
    (fieldName: keyof Partial<IContactForm>, validationResult: ValidationResult) => {
      const [dirtyState] = formValidations[fieldName] || [false];

      setFormValidations({ ...formValidations, [fieldName]: [dirtyState, validationResult] });
    },
    [formValidations]
  );

  const handleDirty = useCallback(
    (fieldName: keyof Partial<IContactForm>, dirty: boolean) => {
      const [, validationResult] = formValidations[fieldName] || [false, { type: "ok" }];

      setFormValidations({ ...formValidations, [fieldName]: [dirty, validationResult] });
    },
    [formValidations]
  );

  useEffect(() => {
    const actionTypeFamily = ContactFormActionNames[CRUDAction.CREATE];
    const unsubscribe = pipe(
      actions$,
      filter((action: IActionWithPayload<string, IEntityCreateSuccess<IContactForm>>) =>
        [actionTypeFamily[RequestAction.SUCCESS], actionTypeFamily[RequestAction.FAILURE]].includes(action.type)
      ),
      subscribe((action: IActionWithPayload<string, IEntityCreateSuccess<IContactForm>>) => {
        if (action.type === actionTypeFamily[RequestAction.FAILURE]) {
          toggleIsSaving(false);
        } else {
          setTimeout(() => {
            const { id } = action.payload;
            history.replace(`${SettingsRoutes.WidgetForms}/edit/${id}`);
          }, 100);
        }
      })
    );

    return unsubscribe;
  }, [history]);

  return isAdmin ? (
    <>
      <Grid relativeWidths={[4, 1]} minWidths={[0, 320]} focusedColumn={0} overflowHidden>
        <ViewStack>
          <SettingsViewContainer
            noBackButton
            title="New contact form"
            leftNodes={
              <BackButton ref={backButtonRef} onClick={handleBack}>
                Back
              </BackButton>
            }
          >
            <PageWrapper>
              <ContentWrapper>
                <ContactForm
                  data={contactForm}
                  onChange={handleChange}
                  onConsentChange={handleConsentChange}
                  onTeamChange={handleTeamChange}
                  onLivechatGroupChange={handleLivechatGroupChange}
                  onValidation={handleValidation}
                  onDirty={handleDirty}
                  validations={formValidations}
                  backButtonRef={backButtonRef}
                />
              </ContentWrapper>
            </PageWrapper>
          </SettingsViewContainer>
        </ViewStack>
      </Grid>
      <FixedSaveBar
        mode={saveBarMode}
        onSave={handleSubmit}
        customSaveLabel="Create form"
        onCancel={handleBack}
        saveDisabled={!isContactFormChanged || !isFormValid}
      />
    </>
  ) : (
    <Redirect to={SettingsRoutes.ContactForms} />
  );
};
